import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BillingCycleTable } from "@/components/billing-cycles";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CalendarClock,
  Filter,
  Loader2,
  RefreshCw,
  ScanSearch,
  Search,
  X,
} from "lucide-react";
import { useBillingCycles } from "@/hooks/useBillingCycleQueries";
import { useOrdersByIds } from "@/hooks/useAdminOrders";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { formatDate } from "@/lib/utils";
import { getPaginationItems } from "@/lib/pagination";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import {
  BILLING_CYCLE_COLUMNS,
  useBillingCycleColumnsStore,
} from "@/stores/billingCycleColumnsStore";
import {
  ALL_FUNDING_SOURCES,
  ALL_STATUSES,
  BILLING_CYCLE_STATUS_OPTIONS,
  FUNDING_SOURCE_OPTIONS,
  useBillingCycleFiltersStore,
} from "@/stores/billingCycleFiltersStore";
import {
  FUNDING_SOURCE_LABELS,
  type BillingCycleStatus,
  type FundingSource,
} from "@/types/billing-cycle";

export function BillingCycleListPage() {
  // Filters, sort, page and page size live in a persisted store, so the view an operator set up
  // survives a reload and a trip to the expiry screen and back.
  const { search, status, fundingSource, createdRange } =
    useBillingCycleFiltersStore((state) => state.filters);
  const page = useBillingCycleFiltersStore((state) => state.page);
  const pageSize = useBillingCycleFiltersStore((state) => state.pageSize);
  const setFilter = useBillingCycleFiltersStore((state) => state.setFilter);
  const setPage = useBillingCycleFiltersStore((state) => state.setPage);
  const setPageSize = useBillingCycleFiltersStore((state) => state.setPageSize);
  const resetFilters = useBillingCycleFiltersStore(
    (state) => state.resetFilters,
  );
  const hasActiveFilters = useBillingCycleFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // The table subscribes to this store itself; the page only needs what the menu renders from.
  const hiddenColumns = useBillingCycleColumnsStore((state) => state.hidden);
  const toggleColumn = useBillingCycleColumnsStore((state) => state.toggle);
  const showAllColumns = useBillingCycleColumnsStore((state) => state.showAll);

  const navigate = useNavigate();

  // Only the half-typed term is local: committing per keystroke would re-query and write
  // sessionStorage per character.
  const [searchInput, setSearchInput] = useState(search);

  // Debounce free-text search so filtering doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      // Guarded because this also runs on mount, where committing the restored value would be a
      // no-op write that still sends the operator back to page 1.
      if (searchInput !== search) {
        setFilter("search", searchInput);
      }
    }, 750);

    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  // Same "both ends or neither" rule the backend enforces — half a picked range is not sent.
  const { fromDate: createdFrom, toDate: createdTo } =
    createdRange.from && createdRange.to
      ? formatDateRangeForAPI(createdRange.from, createdRange.to, false)
      : { fromDate: undefined, toDate: undefined };

  // Empty strings are dropped rather than sent: the backend treats whitespace as "no filter"
  // anyway, and an empty param in the URL makes the query key churn for nothing.
  const filters = {
    createdFrom,
    createdTo,
    search: search || undefined,
    status: status === ALL_STATUSES ? undefined : status,
    fundingSource:
      fundingSource === ALL_FUNDING_SOURCES ? undefined : fundingSource,
    page,
    pageSize,
  };

  const {
    data: response,
    isFetching,
    error,
    refetch,
  } = useBillingCycles(filters);

  // This screen's "start over", the same role Refresh plays on the resource and payment lists: it
  // drops every filter and reloads, always. It used to do one of two different things depending on
  // whether a filter happened to be set — reset when there was one, reload when there was not — so
  // the same button could not be predicted before it was pressed, and its tooltip could only ever
  // describe half of what it did.
  //
  // The local box is emptied here, not just the store. It holds its own copy of what is being
  // typed, so clearing the store alone left the old term on screen AND let its 750ms debounce push
  // it straight back into the filters — the list re-narrowed itself a moment after the reset,
  // which is exactly the bug that was reported on the user list.
  //
  // `resetFilters` and not `clearCache`: the filters and the page go back to their defaults, but
  // the page size the operator chose is theirs and survives.
  const handleRefresh = () => {
    setSearchInput("");
    resetFilters();
    void refetch();
  };

  // A term typed but not yet committed: the debounce has not fired, so nothing is fetching and the
  // table still shows the previous result. The spinner in the search box reads from this.
  const isSearchPending = searchInput !== search;

  const cycles = response?.data || [];

  // The Order column's data, resolved separately: the billing-cycle response carries only
  // `sourceReferenceId`, and one batched lookup for the page is what keeps this from becoming a
  // request per row.
  //
  // Skipped entirely when the operator has hidden the column — an empty list leaves the query
  // disabled, so a screen that is not showing orders does not fetch them. This is the only column
  // whose data costs a request of its own, which is what makes it worth gating; the rest arrive
  // with the list either way.
  //
  // Filtered on the funding source, not merely on the id being present. Every cycle has a
  // `sourceReferenceId`, but it is an ORDER id only when a payment funded it — on a trial it is the
  // sign-up bonus lot and on a transfer it is the previous cycle, so sending those would be a
  // guaranteed miss on every such row, every page.
  const isOrderColumnVisible = !hiddenColumns.includes("order");

  const orderIds = useMemo(
    () =>
      isOrderColumnVisible
        ? cycles
            .filter((cycle) => cycle.fundingSource === "Payment")
            .map((cycle) => cycle.sourceReferenceId)
            .filter((id): id is string => !!id)
        : [],
    [cycles, isOrderColumnVisible],
  );

  const { data: orders, isFetching: isOrdersFetching } =
    useOrdersByIds(orderIds);

  const totalPages = response?.pagination?.totalPages || 1;
  const currentPage = response?.pagination?.currentPage || 1;
  const totalCount = response?.pagination?.totalItems || 0;

  // Only take the screen over when there is genuinely nothing to show. A refetch that fails — a
  // token refresh losing a race, a blip on one filter change — used to replace the table AND the
  // whole filter bar with a Retry card, so the operator had to rebuild their filters to get back to
  // rows that were still on screen a moment earlier. `useBillingCycles` keeps the previous page as
  // placeholder data, so with a response in hand the failure is reported in a banner above the
  // table instead, and Retry is one click away either way.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">
                Failed to load billing cycles
              </p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // The first load, before any page has landed. Naming it keeps the two loading signals apart:
  // skeleton rows while the table is empty, the "Updating..." pill only when it is refreshing rows
  // the operator can still see. Showing both at once — which is what `isFetching` alone did — puts
  // a "still loading" badge over a table that has not drawn a row yet.
  const isReloading = isFetching && cycles.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the card, not the viewport: `fixed inset-0` centred this on the screen, so
            on a short table it hovered in the blank space well below the rows it described. */}
        {isFetching && !isReloading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          {/* One row: the search box an operator reaches for first, then the chips, then the
              buttons. Status and the date range live behind the Filters button, the same split the
              resource page uses. Wraps only below `sm`, where a single line cannot hold them. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap">
            {/* One box, modelled on the resource list: the backend routes by the SHAPE of what was
                pasted, so an operator never has to know which kind of id they are holding before
                they can search for it. A whole GUID is matched by equality against the cycle, the
                resource, the tenant AND the provider's VM UUID — which is what retired the separate
                "Tenant ID" input that used to sit here. The placeholder names every shape that
                routing accepts, so the box says what it can answer. */}
            <div className="relative w-full shrink-0 sm:w-[360px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              {/* The debounce window has no isFetching to report, so the box says so itself: a term
                  typed but not yet committed leaves the table showing the previous result, and
                  without this the screen sits motionless for three quarters of a second after every
                  keystroke and reads as frozen. */}
              {isSearchPending && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                // React withholds onChange while an IME composition is open — which is the whole
                // time a Vietnamese Telex/VNI method is assembling a word — so the term only
                // reached state once a space closed the composition, and the search appeared to
                // need a trailing space to run at all. Same guard as the resource, tenant and
                // payment boxes.
                onCompositionEnd={(event) =>
                  setSearchInput(event.currentTarget.value)
                }
                placeholder="Search VM name, UUID, tenant ID, email, phone"
                aria-label="Search billing cycles"
                className="h-9 pl-9 pr-9"
              />
            </div>

            {/* Sits directly after the search box rather than at the far end of the toolbar: it is
                the only control here that leads somewhere instead of narrowing the list, and next
                to the input it reads as the screen's own action rather than the last of three
                icons. Same placement as New Tenant and Verification Queue. */}
            <Button
              onClick={() => navigate("/admin/billing-cycles/expiry-mismatches")}
              variant="outline"
              size="sm"
              className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm hover:translate-y-0 active:translate-y-0"
            >
              <ScanSearch className="h-4 w-4" />
              Check mismatch
            </Button>

            <div className="order-3 flex w-full min-w-0 items-center gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:justify-end">
              {search !== "" && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Search: {search}
                  <button
                    type="button"
                    onClick={() => {
                      // Both halves, or the box keeps showing a term that no longer filters.
                      setSearchInput("");
                      setFilter("search", "");
                    }}
                    aria-label="Clear search filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}

              {status !== ALL_STATUSES && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Status: {status}
                  <button
                    type="button"
                    onClick={() => setFilter("status", ALL_STATUSES)}
                    aria-label="Clear status filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}

              {fundingSource !== ALL_FUNDING_SOURCES && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Funding: {FUNDING_SOURCE_LABELS[fundingSource]}
                  <button
                    type="button"
                    onClick={() =>
                      setFilter("fundingSource", ALL_FUNDING_SOURCES)
                    }
                    aria-label="Clear funding filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}

              {createdRange.from && createdRange.to && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Created: {formatDate(createdRange.from)} –{" "}
                  {formatDate(createdRange.to)}
                  <button
                    type="button"
                    onClick={() => setFilter("createdRange", {})}
                    aria-label="Clear created date filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            <Popover>
              {/* Icon only, so the accessible name has to come from somewhere: `title` gives the
                  hover tooltip, `aria-label` gives screen readers the same words. The comment sits
                  outside PopoverTrigger on purpose: `asChild` forwards to a single child. */}
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="billingCycleStatusFilter">Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) =>
                      setFilter(
                        "status",
                        value as BillingCycleStatus | typeof ALL_STATUSES,
                      )
                    }
                  >
                    <SelectTrigger
                      id="billingCycleStatusFilter"
                      className="cursor-pointer"
                    >
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_CYCLE_STATUS_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="cursor-pointer"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billingCycleFundingFilter">Funding</Label>
                  <Select
                    value={fundingSource}
                    onValueChange={(value) =>
                      setFilter(
                        "fundingSource",
                        value as FundingSource | typeof ALL_FUNDING_SOURCES,
                      )
                    }
                  >
                    <SelectTrigger
                      id="billingCycleFundingFilter"
                      className="cursor-pointer"
                    >
                      <SelectValue placeholder="Funding" />
                    </SelectTrigger>
                    <SelectContent>
                      {FUNDING_SOURCE_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="cursor-pointer"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billingCycleCreatedDateFilter">
                    Created Date
                  </Label>
                  <DateRangePicker
                    value={createdRange}
                    onChange={(range) => setFilter("createdRange", range)}
                    placeholder="Select date range"
                  />
                  <p className="text-xs text-muted-foreground">
                    Opens on the last six months. Clear it to search all time.
                  </p>
                </div>
              </PopoverContent>
            </Popover>

            <ColumnSettingsMenu
              columns={BILLING_CYCLE_COLUMNS}
              isVisible={(id) => !hiddenColumns.includes(id)}
              toggle={toggleColumn}
              showAll={showAllColumns}
              hiddenCount={hiddenColumns.length}
              className="h-8 min-h-8 shrink-0 px-2 hover:translate-y-0 active:translate-y-0"
            />
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {/* A failure that still left rows on screen is reported here rather than in place of the
              screen, so the filter bar and the last result that loaded both survive it. */}
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}

          {isReloading ? (
            <BillingCycleTable
              cycles={cycles}
              startIndex={(currentPage - 1) * pageSize}
              isLoading
            />
          ) : cycles.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              {/* Fills the card the way the table does instead of collapsing to its own
                  content. CardContent is a min-h-0 flex column, so a fixed-height block here
                  left the card ending part-way down the screen: the whole panel changed size
                  the moment a filter emptied the list, and changed back when it matched
                  again. justify-center then puts the message in the middle of the space the
                  rows occupied rather than tucked under the toolbar. */}
              <CalendarClock className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters
                  ? "No results found"
                  : "No billing cycles found"}
              </h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing the filters."
                  : "Billing cycles appear here once resources are paid for or start a trial."}
              </p>
            </div>
          ) : (
            <BillingCycleTable
              cycles={cycles}
              startIndex={(currentPage - 1) * pageSize}
              orders={orders}
              // Plain `isFetching`, NOT gated on the map being empty. The lookup keeps the previous
              // page's map as placeholder data, so on page 2 a guard like `&& !orders` reads false
              // and every unresolved row falls through to its raw GUID for a moment before the
              // order number replaces it. The cell checks its own order first, so a row already
              // resolved keeps showing it either way — the flag only decides what an UNresolved row
              // does while the request is in flight.
              isOrdersLoading={isOrdersFetching}
            />
          )}

          {/* The footer sits outside the three branches above, so a filter that matched nothing
              still shows its pager, its page-size picker and the row count. It reads
              "Page 1 of 1 (0 total)" with both arrows disabled — a true statement about a result
              that came back, and the row an operator needs to change the page size or step back a
              page after a filter emptied the list.

              `!isReloading` is the one case still held back: on the very first load nothing has
              answered yet, so "0 total" would describe a request still in flight — the same lie the
              skeleton above exists to avoid. */}
          {!isReloading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    // The store returns to page 1 itself when the page size changes.
                    setPageSize(parseInt(value));
                  }}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                {/* Rendered at every page count, not just `totalPages > 1`: hiding the pager on a
                    single page made the controls appear and disappear as filters changed, and a
                    filter that matched nothing lost the page-size picker with them. Previous and
                    Next disable themselves at the ends, so a single page simply shows both
                    greyed out next to page 1. */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Moved down from the toolbar to sit with the other paging controls, the same
                    place the resource and payment lists keep it. It is no longer captioned
                    "Refresh" — the button always throws the filters away, and that word reads as
                    "re-run what I am looking at", which is the opposite. With the caption gone this
                    tooltip is the only warning left, so it names both halves of what happens. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
